'use strict';

import cookie from 'cookie-monster';
import moment from 'moment';

import { getConfig } from './Env';

// Also set by src/Chewba/Action/OidcInit.php
const OIDC_FLOW_COOKIE = 'oidc-flow';

export const OIDC_FLOW_ERROR = 'This sign-in link is not valid or has expired. ' +
    'Please start the Single Sign-On sign-in again.';

/**
 * SSO Utility Functions
 * Centralized functions for SSO operations following EatLove DRY principles
 */

/**
 * Generate cryptographically secure random string for CSRF protection
 * Used for state parameter generation in OIDC flows
 * @returns {string} Random string with high entropy
 */
export function generateSecureRandom() {
    // Use crypto.randomUUID() for maximum entropy when available
    if (window.crypto && window.crypto.randomUUID) {
        return window.crypto.randomUUID().replace(/-/g, '').substring(0, 12);
    }

    // Fallback to crypto.getRandomValues for good entropy
    if (window.crypto && window.crypto.getRandomValues) {
        const array = new Uint32Array(2);
        window.crypto.getRandomValues(array);
        return array[0].toString(36) + array[1].toString(36);
    }

    // Final fallback for very old browsers
    return Math.random().toString(36).substring(2, 11);
}

/**
 * Generate organization initials from organization name
 * Used for fallback avatars when organization logo is not available
 * @param {string} organizationName - Full organization name
 * @returns {string} Organization initials (max 2 characters, uppercase)
 */
export function getOrgInitials(organizationName) {
    if (!organizationName) return 'ORG';

    const words = organizationName.trim().split(/\s+/);
    if (words.length === 1) {
        return words[0].substring(0, 2).toUpperCase();
    }

    return words
        .slice(0, 2)
        .map((word) => word[0])
        .join('')
        .toUpperCase();
}

/**
 * Check if the user is eligible for SSO based on their profile
 * Eligibility is determined by the presence of an authorization URL in user links
 * @param {Object} user - User object containing profile information
 * @returns {boolean} True if user is eligible for SSO, false otherwise
 */
export const isSSOEligible = (user) => {
    if (!user || !user.links) {
        return false;
    }

    return user?.links['authorization-url'];
};

export const SSO_LINK_STATE_PREFIX = 'account_linking_';

/**
 * Parse OIDC state parameter to extract embedded values
 * State format: {base_state}|return_to:{encoded_path}|client_id:{uuid}
 *
 * Real OIDC providers only return code + state in the callback (not client_id separately).
 * The client_id is embedded in the state parameter by our backend
 * so the frontend can recover it after the IdP redirect.
 *
 * @param {string} state - The state parameter from the OIDC callback
 * @returns {Object} Parsed values: { returnTo, clientId, isLink }
 */
export function parseOidcState(state) {
    const result = { returnTo: '/', clientId: null, isLink: false };
    if (!state) return result;

    result.isLink = state.startsWith(SSO_LINK_STATE_PREFIX);

    const clientMatch = state.match(/\|client_id:([^|]+)/);
    if (clientMatch) result.clientId = clientMatch[1];

    const returnMatch = state.match(/\|return_to:([^|]+)/);
    if (returnMatch) {
        const decoded = decodeURIComponent(returnMatch[1]);
        // Security: only allow same-origin paths
        if (decoded.startsWith('/') && !decoded.includes('//')) {
            result.returnTo = decoded;
        }
    }

    return result;
}

/**
 * Check if a callback state comes from a users API /sso/init-authorization flow.
 * The users API stores the nonce of such a flow under its state.
 * @param {string} state - The state parameter from the OIDC callback
 * @returns {boolean} True for an /sso/init-authorization state
 */
export function isOidcInitState(state) {
    return !!state && state.startsWith('oidc_init_');
}

function oidcFlowCookieOptions(expires) {
    return {
        expires,
        domain: getConfig('www_host').replace('www.', '.'),
        path: '/',
        secure: true,
    };
}

/**
 * Remember the state and nonce of an OIDC authorization URL for this browser.
 * The cookie is on the parent domain, so a flow started on PRO can end on the consumer app.
 * @param {string} authorizationUrl - The IdP authorization URL from the users API
 */
export function rememberOidcFlow(authorizationUrl) {
    const params = new URL(authorizationUrl).searchParams;
    const flow = JSON.stringify({ state: params.get('state'), nonce: params.get('nonce') });

    cookie.setItem(OIDC_FLOW_COOKIE, flow, oidcFlowCookieOptions(moment().add(15, 'minutes').toDate().toUTCString()));
}

/**
 * Take the remembered OIDC flow (one use only) and compare its state with the callback state
 * @param {string} state - The state parameter from the OIDC callback
 * @returns {string|null} The nonce of the flow when the states match, null otherwise
 */
export function takeOidcFlowNonce(state) {
    const flow = cookie.getItem(OIDC_FLOW_COOKIE);

    cookie.setItem(OIDC_FLOW_COOKIE, '', oidcFlowCookieOptions(new Date(0).toUTCString()));

    if (!flow || !state) {
        return null;
    }

    let parsed;
    try {
        parsed = JSON.parse(flow);
    } catch {
        return null;
    }

    const { state: storedState, nonce } = parsed;

    return storedState === state ? nonce : null;
}
